import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { AdminView } from "@/components/AdminView";
import { CircuitsView } from "@/components/circuits/CircuitsView";
import { CliAuthView } from "@/components/CliAuthView";
import { ConversationView } from "@/components/ConversationView";
import { DocsView } from "@/components/DocsView";
import { DownloadView } from "@/components/DownloadView";
import { LoginPage } from "@/components/LoginPage";
import { MoriartyConfig } from "@/components/moriarty/MoriartyConfig";
import { MoriartyCosts } from "@/components/moriarty/MoriartyCosts";
import { MoriartyDetail } from "@/components/moriarty/MoriartyDetail";
import { MoriartyList } from "@/components/moriarty/MoriartyList";
import { PreferencesView } from "@/components/PreferencesView";
import { CodexRuleView } from "@/components/reviews/CodexRuleView";
import { CodexView } from "@/components/reviews/CodexView";
import { GitHubReviewsView } from "@/components/reviews/GitHubReviewsView";
import { Sidebar } from "@/components/Sidebar";
import { Toast } from "@/components/Toast";
import { UsageStatsView } from "@/components/UsageStatsView";
import { WelcomeView } from "@/components/WelcomeView";
import { useAnswerReadyNotifications } from "@/hooks/useAnswerReadyNotifications";
import { useModels } from "@/hooks/useModels";
import { usePreferences } from "@/hooks/usePreferences";
import { useSession } from "@/lib/auth";
import { useStore } from "@/lib/store";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000, // Consider data stale after 1 second
      retry: 1,
    },
  },
});

const FONT_FACE_STORAGE_KEY = "sherlog-font-face";

function readStoredFontFace(): "monospace" | "inter" | null {
  if (typeof window === "undefined") {
    return null;
  }

  const value = window.localStorage.getItem(FONT_FACE_STORAGE_KEY);
  return value === "monospace" || value === "inter" ? value : null;
}

function MainContent(): React.JSX.Element {
  const activeView = useStore((state) => state.activeView);
  const activeSessionKey = useStore((state) => state.activeSessionKey);

  if (activeView === "admin") return <AdminView />;
  if (activeView === "circuits") return <CircuitsView />;
  if (activeView === "cli-auth") return <CliAuthView />;
  if (activeView === "moriarty") return <MoriartyList />;
  if (activeView === "moriarty-detail") return <MoriartyDetail />;
  if (activeView === "moriarty-config") return <MoriartyConfig />;
  if (activeView === "moriarty-costs") return <MoriartyCosts />;
  if (activeView === "preferences") return <PreferencesView />;
  if (activeView === "github-reviews") return <GitHubReviewsView />;
  if (activeView === "github-reviews-codex") return <CodexView />;
  if (activeView === "github-reviews-codex-rule") {
    const ruleId = new URLSearchParams(window.location.search).get("rule");
    return ruleId ? <CodexRuleView ruleId={ruleId} /> : <CodexView />;
  }
  if (activeView === "docs") return <DocsView />;
  if (activeView === "download") return <DownloadView />;
  if (activeView === "stats") return <UsageStatsView />;
  if (activeSessionKey) return <ConversationView />;
  return <WelcomeView />;
}

function Dashboard() {
  const selectSession = useStore((state) => state.selectSession);
  const selectMoriartyEnv = useStore((state) => state.selectMoriartyEnv);
  const setDocSlug = useStore((state) => state.setDocSlug);
  const setView = useStore((state) => state.setView);
  const setUser = useStore((state) => state.setUser);
  const setComposerModelId = useStore((state) => state.setComposerModelId);
  const { data: sessionData } = useSession();
  useAnswerReadyNotifications({ enabled: !!sessionData?.user });
  const { data: preferencesData, isPending: isPreferencesPending } = usePreferences();
  const { data: modelsData, isPending: isModelsPending } = useModels();
  const defaultsHydratedRef = useRef(false);

  // Sync user to store when session loads, fetching role from /v1/me
  useEffect(() => {
    if (sessionData?.user) {
      // Set initial user data from session immediately
      setUser({
        id: sessionData.user.id,
        name: sessionData.user.name,
        email: sessionData.user.email,
        image: sessionData.user.image ?? null,
        role: "member", // default until /v1/me responds
      });

      // Fetch role from the API
      fetch("/v1/me")
        .then((res) => res.json())
        .then((data: { role?: string }) => {
          if (data.role) {
            setUser({
              id: sessionData.user.id,
              name: sessionData.user.name,
              email: sessionData.user.email,
              image: sessionData.user.image ?? null,
              role: data.role as "admin" | "member",
            });
          }
        })
        .catch(() => {
          // Ignore — role defaults to "member"
        });
    }
  }, [sessionData?.user, setUser]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const storedFontFace = readStoredFontFace();
    const fontFace = storedFontFace ?? preferencesData?.preferences.fontFace ?? "monospace";
    document.documentElement.dataset.fontFace = fontFace;

    if (!storedFontFace && preferencesData?.preferences.fontFace) {
      window.localStorage.setItem(FONT_FACE_STORAGE_KEY, preferencesData.preferences.fontFace);
    }
  }, [preferencesData?.preferences.fontFace]);

  useEffect(() => {
    if (defaultsHydratedRef.current) {
      return;
    }

    if (isPreferencesPending || isModelsPending) {
      return;
    }

    const modelId =
      preferencesData?.preferences.defaultModelId ?? modelsData?.defaultModelId ?? null;

    setComposerModelId(modelId);
    defaultsHydratedRef.current = true;
  }, [
    isPreferencesPending,
    isModelsPending,
    preferencesData?.preferences.defaultModelId,
    modelsData?.defaultModelId,
    setComposerModelId,
  ]);

  // Sync URL → Store on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const thread = params.get("thread");
    const moriarty = params.get("moriarty");
    const view = params.get("view");
    const doc = params.get("doc");
    if (view === "download") {
      setView("download");
    } else if (thread) {
      selectSession(thread);
    } else if (moriarty) {
      selectMoriartyEnv(moriarty);
    } else if (view === "docs") {
      if (doc) {
        setDocSlug(doc);
      } else {
        setView("docs");
      }
    } else if (view === "admin") {
      setView("admin");
    } else if (view === "cli-auth") {
      setView("cli-auth");
    } else if (view === "moriarty-costs") {
      setView("moriarty-costs");
    } else if (view === "moriarty-config") {
      setView("moriarty-config");
    } else if (view === "moriarty") {
      setView("moriarty");
    } else if (view === "circuits") {
      setView("circuits");
    } else if (view === "github-reviews") {
      if (params.get("section") === "codex") {
        setView(params.get("rule") ? "github-reviews-codex-rule" : "github-reviews-codex");
      } else {
        setView("github-reviews");
      }
    } else if (view === "stats") {
      setView("stats");
    } else if (view === "preferences") {
      setView("preferences");
    } else {
      setView("sessions");
    }
  }, [selectSession, selectMoriartyEnv, setDocSlug, setView]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const navigationBlockUrl = useStore.getState().navigationBlockUrl;
      if (navigationBlockUrl) {
        window.history.pushState({}, "", navigationBlockUrl);
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const thread = params.get("thread");
      const moriarty = params.get("moriarty");
      const view = params.get("view");
      const doc = params.get("doc");
      if (view === "download") {
        setView("download");
      } else if (thread) {
        selectSession(thread);
      } else if (moriarty) {
        selectMoriartyEnv(moriarty);
      } else if (view === "docs") {
        if (doc) {
          setDocSlug(doc);
        } else {
          setView("docs");
        }
      } else if (view === "admin") {
        setView("admin");
      } else if (view === "cli-auth") {
        setView("cli-auth");
      } else if (view === "moriarty-costs") {
        setView("moriarty-costs");
      } else if (view === "moriarty-config") {
        setView("moriarty-config");
      } else if (view === "moriarty") {
        setView("moriarty");
      } else if (view === "circuits") {
        setView("circuits");
      } else if (view === "github-reviews") {
        if (params.get("section") === "codex") {
          setView(params.get("rule") ? "github-reviews-codex-rule" : "github-reviews-codex");
        } else {
          setView("github-reviews");
        }
      } else if (view === "stats") {
        setView("stats");
      } else if (view === "preferences") {
        setView("preferences");
      } else {
        setView("sessions");
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [selectSession, selectMoriartyEnv, setDocSlug, setView]);

  return (
    <div className="flex h-screen flex-col md:flex-row">
      <Sidebar />
      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        <MainContent />
      </main>
    </div>
  );
}

function AuthenticatedApp() {
  const { data: sessionData, isPending } = useSession();

  // Show loading state while checking auth
  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-cyber-muted">Loading...</div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!sessionData?.user) {
    return <LoginPage />;
  }

  // Show dashboard if authenticated
  return <Dashboard />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <div className="scanlines bg-cyber-bg h-screen overflow-hidden">
        <div className="relative z-10 h-full">
          <AuthenticatedApp />
        </div>
      </div>
      <Toast />
    </QueryClientProvider>
  );
}
